import { SDKConfig, EditorConfig } from '@core/types';
import { DEFAULT_CONFIG, DEFAULT_ENDPOINTS } from '@shared/constants';
import { ConfigurationError } from '@shared/errors';

export class ConfigManager {
  private config: SDKConfig | EditorConfig;
  private isEditor: boolean;

  constructor(config: SDKConfig | EditorConfig, isEditor: boolean = false) {
    this.isEditor = isEditor;
    this.validate(config);
    this.config = this.normalize(config);
  }

  private validate(config: SDKConfig | EditorConfig): void {
    if (!config.siteKey) {
      throw new ConfigurationError('siteKey is required');
    }

    if (this.isEditor && !('sessionToken' in config)) {
      throw new ConfigurationError('sessionToken is required for editor mode');
    }

    if ('sessionToken' in config && !config.sessionToken) {
      throw new ConfigurationError('sessionToken must be a string');
    }
  }

  private normalize(config: SDKConfig | EditorConfig): SDKConfig | EditorConfig {
    return {
      ...DEFAULT_CONFIG,
      ...config,
      endpoints: {
        ...DEFAULT_ENDPOINTS,
        ...config.endpoints,
      },
    };
  }

  get<K extends keyof SDKConfig>(key: K): SDKConfig[K] {
    return this.config[key];
  }

  getEndpoint(name: keyof typeof DEFAULT_ENDPOINTS): string {
    const endpoints = this.config.endpoints || DEFAULT_ENDPOINTS;
    const endpoint = endpoints[name];
    if (endpoint) return endpoint;
    const defaultEndpoint = DEFAULT_ENDPOINTS[name];
    if (defaultEndpoint) return defaultEndpoint;
    throw new Error(`Unknown endpoint: ${name}`);
  }

  isEditorMode(): boolean {
    return this.isEditor;
  }

  getFullConfig(): Readonly<SDKConfig | EditorConfig> {
    return { ...this.config };
  }

  update<K extends keyof SDKConfig>(key: K, value: SDKConfig[K]): void {
    this.config = { ...this.config, [key]: value };
  }
}
